import React, { ComponentType } from 'react';
import { Theme, createTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import type { GetPublicWhiteLabelSettingsQuery } from '../../gql/graphql';

export const INSURANCE_TYPES = ['LIFE', 'COMMERCIAL', 'PERSONAL'] as const;
export type InsuranceType = typeof INSURANCE_TYPES[number];

type PublicWhiteLabelSettings = NonNullable<
  GetPublicWhiteLabelSettingsQuery['publicWhiteLabelSettings']
>;

export type CustomButton = Pick<
  PublicWhiteLabelSettings['customButtons'][number],
  'title' | 'url'
>;

export interface WebsiteSettingsInterface {
  readonly slug: string;
  readonly host: string;
  readonly personalInsurance: boolean;
  readonly companyInsurType: string;
  readonly companyName: string;
  readonly hours: string;
  readonly phone: string;
  readonly email: string;
  readonly chatLink: string;
  readonly privacyLink: string;
  readonly companyTheme: Theme;
  readonly logo: string;
  readonly headerLogo?: string;
  readonly footerLogo?: string;
  readonly businessFilter: (business: $TSFixMe) => boolean;
  readonly tileIcons: (internalName: string) => React.ReactNode;
  readonly title: string;
  readonly favicon: string;
  readonly mobileLogo: string;
  readonly squareLogo: string;
  readonly thankYouText: string;
  readonly policystreamIntroductoryText: string;
  readonly sendEmailOnContactInformationCompleted: boolean;
  readonly contactInformationFirst: boolean;
  readonly headerCss?: $TSFixMe; // TODO this should be whatever type a CSS object is
  readonly footerCss?: $TSFixMe; // TODO this should be whatever type a CSS object is
  readonly Footer?: ComponentType;
  readonly allowApplicationUpload: boolean;
  readonly types: Array<InsuranceType>;
  readonly homepage?: string; // homepage for the company; generally used for linking when done with an application
  readonly aiAssistedBusinessSelection: boolean;
  readonly customButtonList: Array<CustomButton>;
  readonly googleAnalyticsToken?: string;
}

/**
 * The subset of white-label configuration that cannot be stored in the
 * database and therefore lives on the frontend.  Each entry in the
 * frontendOnlyBySlug map carries exactly these fields; everything else
 * (company name, phone, colours, logos, themes, …) comes from the backend at
 * runtime.
 *
 * `personalInsurance` and `companyInsurType` are derived by `WebsiteSettings`
 * from `types`, not stored separately.
 */
export type FrontendOnlyPieces = Pick<WebsiteSettingsInterface, 'slug'> &
  Partial<
    Pick<WebsiteSettingsInterface, 'businessFilter' | 'tileIcons' | 'Footer'>
  >;

/**
 *  @param slug - a unique identifier for the particular settings
 *  @param host - the website host (used for matching)
 *  @param personalInsurance - does this brokerage provide personal insurance
 *  @param companyInsurType - what types of insurance this company offers
 *  @param companyName - The displayed name of the company or organization
 *  @param hours - business hours in a human-readable string
 *  @param phone - a phone number to reach them
 *  @param email: a contact email
 *  @param chatLink: a link to an online chat
 *  @param privacyLink: a link to the privacy policy
 *  @param companyTheme: the theme for this particular whitelabel
 *  @param logo: a logo to be displayed
 *  @param headerLogo: optional logo for the header (falls back to logo if not set)
 *  @param footerLogo: optional logo for the footer (falls back to logo if not set)
 *  @param businessFilter: a function which returns true for businesses that this whitelabel can handle and false otherwise
 *  @param tileIcons: a function that returns custom icons for use in the TileSelector
 *  @param title: The website title for this whitelabel
 *  @param favicon: The favicon
 *  @param mobileLogo: a logo for mobile
 *  @param thankYouText: The text that notifies the user that they will be contacted within 24-48 hours
 *  @param sendEmailOnContactInformationCompleted: whether or not to send an email and display an indicator page when they have finished completing the contact information question
 *  @param contactInformationFirst - Should a form with the contact information questions be presented before the policy selection page?
 *  @param headerCss - CSS to apply to the header (quotewizard) - actually stands in the sx prop, so you can use theme-specific stuff here
 *  @param footer
 *  @param Footer - A component to use for the footer in the QuoteWizard
 *  @param allowApplicationUpload - should we display the upload application button (generates an application from a pdf or whatnot)
 *  @param types - what type of insurance this whitelabel is for
 *  @param homepage - homepage for the company. Generally used for linking after a completed application.
 *  @param customButtonList - custom button object that contains the title of the button as well as the link associated. Its a list incase there are multiple buttons
 *  @param googleAnalyticsToken - the org's GoogleAnalytics token
 */
export class WebsiteSettings implements WebsiteSettingsInterface {
  slug: string;
  host: string;
  /**
   * @deprecated use type instead
   */
  personalInsurance: boolean;
  /**
   * @deprecated use type instead
   */
  companyInsurType: string;
  companyName: string;
  hours: string;
  phone: string;
  email: string;
  chatLink: string;
  privacyLink: string;
  companyTheme: Theme;
  logo: string;
  headerLogo?: string;
  footerLogo?: string;
  businessFilter: (business: $TSFixMe) => boolean;
  tileIcons: (internalName: string) => React.ReactNode;
  title: string;
  favicon: string;
  mobileLogo: string;
  squareLogo: string;
  sendEmailOnContactInformationCompleted: boolean;
  contactInformationFirst: boolean;
  thankYouText: string;
  policystreamIntroductoryText: string;
  headerCss: $TSFixMe;
  footerCss?: $TSFixMe;
  Footer?: ComponentType;
  allowApplicationUpload: boolean;
  types: Array<InsuranceType>;
  homepage?: string;
  aiAssistedBusinessSelection: boolean;
  customButtonList: Array<CustomButton>;
  googleAnalyticsToken?: string;

  constructor({
    slug = uuidv4(),
    host = '',
    companyName = '',
    hours = '',
    phone = '',
    email = '',
    chatLink = '',
    privacyLink = '',
    companyTheme = createTheme(),
    logo = '',
    headerLogo,
    footerLogo,
    businessFilter = (_business: $TSFixMe) => true,
    tileIcons = (_internalName: $TSFixMe) => undefined,
    title = '',
    favicon = '',
    mobileLogo = '',
    squareLogo = '',
    sendEmailOnContactInformationCompleted = false,
    // sendEmailOnContactInformationCompleted = true, // for testing only
    contactInformationFirst = false,
    headerCss = undefined,
    footerCss = undefined,
    thankYouText = '',
    policystreamIntroductoryText = '',
    Footer,
    allowApplicationUpload = false,
    types = [],
    homepage,
    aiAssistedBusinessSelection = false,
    customButtonList = [],
    googleAnalyticsToken,
  }: Partial<WebsiteSettingsInterface>) {
    this.slug = slug;
    this.host = host;
    this.personalInsurance = derivePersonalInsurance(types);
    this.companyInsurType = deriveCompanyInsuranceType(types);
    this.companyName = companyName;
    this.hours = hours;
    this.phone = phone;
    this.email = email;
    this.chatLink = chatLink;
    this.privacyLink = privacyLink;
    this.companyTheme = companyTheme;
    this.logo = logo;
    this.headerLogo = headerLogo;
    this.footerLogo = footerLogo;
    this.businessFilter = businessFilter;
    this.tileIcons = tileIcons;
    this.title = title;
    this.favicon = favicon;
    this.mobileLogo = mobileLogo;
    this.squareLogo = squareLogo;
    this.sendEmailOnContactInformationCompleted =
      sendEmailOnContactInformationCompleted;
    this.contactInformationFirst = contactInformationFirst;
    this.thankYouText = thankYouText;
    this.policystreamIntroductoryText = policystreamIntroductoryText;
    this.headerCss = headerCss;
    this.footerCss = footerCss;
    this.Footer = Footer;
    this.allowApplicationUpload = allowApplicationUpload;
    this.types = types;
    this.homepage = homepage;
    this.aiAssistedBusinessSelection = aiAssistedBusinessSelection;
    this.customButtonList = customButtonList;
    this.googleAnalyticsToken = googleAnalyticsToken;
  }
}

function derivePersonalInsurance(types: Array<InsuranceType>): boolean {
  return types.includes('PERSONAL');
}

function deriveCompanyInsuranceType(types: Array<InsuranceType>): string {
  const hasPersonal = types.includes('PERSONAL');
  const hasCommercial = types.includes('COMMERCIAL');
  if (hasPersonal && hasCommercial) return 'both';
  if (hasPersonal) return 'personal';
  if (hasCommercial) return 'commercial';
  return 'both';
}
